import { useState, useEffect } from "react";
import AppShell from "@/components/layout/AppShell";
import api, { formatApiErrorDetail } from "@/lib/apiClient";
import { Button } from "@/components/ui/button";
import { toast } from "sonner";
import { Warning, Database, ArrowClockwise, Trash, CheckCircle, CloudArrowUp } from "@phosphor-icons/react";
import {
  Dialog, DialogContent, DialogDescription, DialogFooter, DialogHeader, DialogTitle,
} from "@/components/ui/dialog";
import { useAuth } from "@/context/AuthContext";
import { Navigate } from "react-router-dom";

const PURGE_PRESETS = [
  { days: 90, label: "90 Hari (aman)",     color: "bg-slate-600 hover:bg-slate-700", risk: "aman" },
  { days: 60, label: "60 Hari",            color: "bg-slate-600 hover:bg-slate-700", risk: "aman" },
  { days: 30, label: "30 Hari (agresif)",  color: "bg-amber-600 hover:bg-amber-700", risk: "agresif" },
  { days: 14, label: "14 Hari (darurat)",  color: "bg-orange-600 hover:bg-orange-700", risk: "darurat" },
  { days: 7,  label: "7 Hari (kritis)",    color: "bg-red-600 hover:bg-red-700",     risk: "kritis" },
];

export default function StorageMaintenancePage() {
  const { user } = useAuth();
  const [stats, setStats] = useState(null);
  const [loading, setLoading] = useState(false);
  const [confirmPurge, setConfirmPurge] = useState(null);
  const [purging, setPurging] = useState(false);
  const [lastResult, setLastResult] = useState(null);
  const [migrating, setMigrating] = useState(false);
  const [migrateProgress, setMigrateProgress] = useState(null);

  const loadStats = async () => {
    setLoading(true);
    try {
      const r = await api.get("/admin/photo-storage-stats");
      setStats(r.data);
    } catch (e) {
      toast.error("Gagal ambil statistik foto");
    } finally { setLoading(false); }
  };

  useEffect(() => { loadStats(); }, []);

  const runPurge = async () => {
    if (!confirmPurge) return;
    setPurging(true);
    try {
      const r = await api.post("/admin/purge-photos-now", { days: confirmPurge.days });
      setLastResult(r.data);
      setConfirmPurge(null);
      toast.success(`Purge selesai: ${r.data.docs_purged} dokumen dibersihkan`);
      loadStats();
    } catch (e) {
      toast.error(formatApiErrorDetail(e.response?.data?.detail) || e.message);
    } finally { setPurging(false); }
  };

  const runMigration = async () => {
    setMigrating(true);
    const acc = { photos: 0, docs: 0, batches: 0, done: false, errors: [] };
    setMigrateProgress({ ...acc });
    try {
      for (let i = 0; i < 200; i++) {
        const r = await api.post("/admin/migrate-photos-to-storage", { limit: 50 });
        acc.photos += r.data.photos_uploaded;
        acc.docs += r.data.docs_updated;
        acc.batches += 1;
        if (r.data.errors) acc.errors = r.data.errors;
        acc.done = !r.data.has_more;
        setMigrateProgress({ ...acc });
        if (acc.done || r.data.docs_updated === 0) break;
      }
      if (acc.done) toast.success(`Migrasi selesai: ${acc.photos} foto dipindahkan`);
      else toast.warning("Migrasi berhenti — sebagian foto gagal dipindahkan. Coba lagi nanti.");
      loadStats();
    } catch (e) {
      toast.error(formatApiErrorDetail(e.response?.data?.detail) || e.message);
    } finally { setMigrating(false); }
  };

  if (user && !["admin", "owner"].includes(user.role)) return <Navigate to="/" replace />;

  const total = stats?.stats.reduce((a, b) => a + b.docs_with_photos, 0) || 0;

  return (
    <AppShell
      title="Storage Maintenance"
      subtitle="Kelola penyimpanan foto Base64 di MongoDB. Purge foto lama saat storage penuh."
      actions={
        <Button onClick={loadStats} disabled={loading} data-testid="storage-refresh" className="gap-2">
          <ArrowClockwise size={14} weight="bold" className={loading ? "animate-spin" : ""} /> Refresh
        </Button>
      }
    >
      {/* Warning banner */}
      <div className="rounded-xl bg-amber-50 border border-amber-200 p-4 mb-6 flex items-start gap-3">
        <Warning size={22} weight="fill" className="text-amber-600 shrink-0 mt-0.5" />
        <div className="text-[12px] text-amber-900">
          <b>Kapan pakai halaman ini?</b> Saat MongoDB Atlas melaporkan disk hampir penuh dan sistem lambat / gagal simpan data. Purge <b>hanya membersihkan foto</b> pada dokumen lama — metadata (nomor slip, berat, tanggal, dst) tetap tersimpan. Foto yang sudah di-purge tidak bisa dikembalikan.
        </div>
      </div>

      {/* Stats table */}
      <div className="rounded-xl bg-white border border-slate-200 shadow-sm overflow-hidden mb-6">
        <div className="px-5 py-3 border-b border-slate-100 bg-slate-50 flex items-center gap-2">
          <Database size={16} weight="duotone" className="text-slate-600" />
          <h3 className="font-heading text-sm font-bold uppercase tracking-wider text-slate-800">Dokumen Berfoto per Collection</h3>
          <span className="ml-auto text-[11px] font-mono text-slate-500">Total: {total.toLocaleString("id-ID")}</span>
        </div>
        {!stats ? (
          <div className="p-8 text-center text-xs text-slate-400">{loading ? "Memuat…" : "Klik Refresh untuk memuat"}</div>
        ) : (
          <div className="overflow-x-auto">
            <table className="w-full text-[12px]" data-testid="storage-stats-table">
              <thead className="bg-slate-50 text-[10px] uppercase tracking-wider text-slate-500">
                <tr>
                  <th className="text-left px-4 py-2">Collection</th>
                  <th className="text-right px-4 py-2">Total Foto</th>
                  <th className="text-right px-4 py-2">≥ 7 hari</th>
                  <th className="text-right px-4 py-2">≥ 14 hari</th>
                  <th className="text-right px-4 py-2">≥ 30 hari</th>
                  <th className="text-right px-4 py-2">≥ 60 hari</th>
                  <th className="text-right px-4 py-2">≥ 90 hari</th>
                </tr>
              </thead>
              <tbody className="divide-y divide-slate-100">
                {stats.stats.map((s) => (
                  <tr key={s.collection} className="hover:bg-slate-50/70">
                    <td className="px-4 py-2 font-mono font-semibold text-slate-800">{s.collection}</td>
                    <td className="px-4 py-2 font-mono text-right font-bold text-slate-800">{s.docs_with_photos.toLocaleString("id-ID")}</td>
                    {["7","14","30","60","90"].map((d) => (
                      <td key={d} className="px-4 py-2 font-mono text-right text-slate-600">{(s.older_than_days[d] || 0).toLocaleString("id-ID")}</td>
                    ))}
                  </tr>
                ))}
              </tbody>
            </table>
          </div>
        )}
      </div>

      {/* Migrate legacy base64 → object storage */}
      <div className="rounded-xl bg-white border border-sky-200 shadow-sm p-5 mb-6" data-testid="migrate-card">
        <div className="flex items-center gap-2 mb-1">
          <CloudArrowUp size={18} weight="duotone" className="text-sky-600" />
          <h3 className="font-heading text-sm font-bold uppercase tracking-wider text-slate-800">Migrasi Foto Lama ke Object Storage</h3>
        </div>
        <p className="text-[12px] text-slate-500 mb-4">
          Pindahkan foto Base64 lama dari MongoDB ke Object Storage. Foto <b>tidak dihapus</b> — tetap bisa dilihat seperti biasa, tetapi database jadi jauh lebih ringan. Aman dijalankan berulang kali.
        </p>
        <Button onClick={runMigration} disabled={migrating} data-testid="migrate-photos-btn" className="gap-2 bg-sky-600 hover:bg-sky-700 text-white">
          <CloudArrowUp size={14} weight="bold" className={migrating ? "animate-pulse" : ""} />
          {migrating ? "Memindahkan…" : "Mulai Migrasi Foto"}
        </Button>
        {migrateProgress && (
          <div className="mt-4 rounded-md bg-sky-50 border border-sky-100 px-4 py-3 text-[12px] text-sky-900" data-testid="migrate-progress">
            Batch: <b className="font-mono">{migrateProgress.batches}</b> · Dokumen: <b className="font-mono">{migrateProgress.docs.toLocaleString("id-ID")}</b> · Foto dipindahkan: <b className="font-mono">{migrateProgress.photos.toLocaleString("id-ID")}</b>
            {!migrating && (
              <span className={`ml-2 font-bold ${migrateProgress.done ? "text-emerald-700" : "text-amber-700"}`} data-testid="migrate-status">
                {migrateProgress.done ? "✓ Semua foto sudah di Object Storage" : "Belum tuntas"}
              </span>
            )}
            {migrateProgress.errors.length > 0 && (
              <div className="mt-1 text-red-700 font-mono text-[11px]">{migrateProgress.errors.join(" | ")}</div>
            )}
          </div>
        )}
      </div>

      {/* Purge actions */}
      <div className="rounded-xl bg-white border border-slate-200 shadow-sm p-5">
        <h3 className="font-heading text-sm font-bold uppercase tracking-wider text-slate-800 mb-1">Purge Foto</h3>
        <p className="text-[12px] text-slate-500 mb-4">
          Pilih retensi (usia minimum foto yang dipertahankan). Semua foto <b>lebih tua dari itu</b> akan dihapus dari MongoDB.
        </p>
        <div className="flex flex-wrap gap-2">
          {PURGE_PRESETS.map((p) => (
            <button
              key={p.days}
              onClick={() => setConfirmPurge(p)}
              data-testid={`purge-btn-${p.days}`}
              className={`${p.color} text-white text-[12px] font-bold px-4 py-2 rounded-md shadow-sm flex items-center gap-2`}
            >
              <Trash size={13} weight="fill" />
              Purge {p.label}
            </button>
          ))}
        </div>
        <p className="text-[10px] text-slate-400 mt-4">
          Rekomendasi: <b>90 hari</b> untuk maintenance rutin. Gunakan <b>30 / 14 / 7</b> hanya saat MongoDB storage kritis.
        </p>
      </div>

      {/* Last result */}
      {lastResult && (
        <div className="rounded-xl bg-emerald-50 border border-emerald-200 p-5 mt-6" data-testid="purge-result">
          <div className="flex items-center gap-2 mb-2">
            <CheckCircle size={18} weight="fill" className="text-emerald-600" />
            <h3 className="font-heading text-sm font-bold uppercase tracking-wider text-emerald-900">
              Purge Selesai — {lastResult.docs_purged.toLocaleString("id-ID")} dokumen dibersihkan
            </h3>
          </div>
          <div className="text-[11px] text-emerald-800 mb-3">
            Retensi: <b>{lastResult.retention_days} hari</b> · Cutoff: <span className="font-mono">{lastResult.cutoff}</span>
          </div>
          <div className="grid grid-cols-2 md:grid-cols-4 gap-2 text-[11px]">
            {Object.entries(lastResult.per_collection || {}).map(([coll, n]) => (
              <div key={coll} className="bg-white rounded-md px-3 py-2 border border-emerald-100">
                <div className="text-[9px] uppercase tracking-wider text-slate-500">{coll}</div>
                <div className="font-mono font-bold text-slate-800 mt-0.5">{n.toLocaleString("id-ID")}</div>
              </div>
            ))}
          </div>
        </div>
      )}

      {/* Confirm dialog */}
      <Dialog open={!!confirmPurge} onOpenChange={(o) => { if (!o && !purging) setConfirmPurge(null); }}>
        <DialogContent className="max-w-lg" data-testid="purge-confirm-dialog">
          <DialogHeader>
            <DialogTitle className="font-heading text-xl flex items-center gap-2">
              <Warning size={22} weight="fill" className="text-red-600" />
              Konfirmasi Purge Foto
            </DialogTitle>
            <DialogDescription className="pt-2 text-[13px]">
              Aksi ini akan <b>menghapus semua foto</b> di dokumen yang berumur lebih dari <b>{confirmPurge?.days} hari</b> dari MongoDB. Metadata (nomor slip, berat, tanggal) tetap aman. Foto yang di-purge <b>TIDAK DAPAT dikembalikan</b>.
            </DialogDescription>
          </DialogHeader>
          {confirmPurge && stats && (
            <div className="space-y-3 mt-2">
              <div className="rounded-lg bg-slate-50 border border-slate-200 p-3 text-[12px]">
                <div className="text-[10px] font-bold uppercase tracking-wide text-slate-500 mb-1">Estimasi Dokumen yang Akan Di-purge</div>
                <div className="grid grid-cols-2 gap-2">
                  {stats.stats.map((s) => {
                    const n = s.older_than_days[String(confirmPurge.days)] || 0;
                    if (n === 0) return null;
                    return (
                      <div key={s.collection} className="flex justify-between font-mono">
                        <span className="text-slate-700">{s.collection}</span>
                        <span className="font-bold text-red-700">{n.toLocaleString("id-ID")}</span>
                      </div>
                    );
                  })}
                </div>
              </div>
            </div>
          )}
          <DialogFooter>
            <Button variant="outline" onClick={() => setConfirmPurge(null)} disabled={purging}>Batal</Button>
            <Button
              onClick={runPurge}
              disabled={purging}
              data-testid="purge-confirm-btn"
              className={`${confirmPurge?.color || "bg-red-600"} text-white`}
            >
              {purging ? "Membersihkan…" : `Ya, Purge Foto > ${confirmPurge?.days} Hari`}
            </Button>
          </DialogFooter>
        </DialogContent>
      </Dialog>
    </AppShell>
  );
}
